import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { selectToken } from "../app/slices/AuthenticationSlice";
import { selectCurrentTrainingId } from "../app/slices/ProjectsSlice";
import { selectCurrentOrganization } from "../app/slices/UserSlice";
import { TranslationWithDescriptionDTO } from "../dto/TranslationWithDescriptionDTO";
import { TrainingService } from "../services/TrainingService";
import { useSaveTraining } from "./SaveTrainingHook";
import { selectCurrentEnvironmentId, selectEnvironmentInteractionPoints } from "../app/slices/EnvironmentSlice";
import {
  selectLocalizedEntries,
  selectLocalizedLanguages,
  selectPropLocalizedEntries,
} from "../app/slices/TrainingLocalizationSlice";
import { selectReferenceLanguage, selectTakes, selectAllPropPresets } from "../app/slices/TrainingSlice";
import { selectUsedProps } from "../app/slices/PropLibrarySlice";
import { getTakeStartingViewErrors } from "../helpers/TakeStartingView";
import Take3D from "../models/Take3D";
import { TranslationService } from "../services/TranslationService";
import { isNullOrWhitespace } from "../helpers/StringHelper";
import {
  classifyPublishFailure,
  PUBLISH_TRANSLATION_FAILED_CODE,
  PublishFailure,
} from "../features/publish_course/publishErrors";
import {
  clearPublishDraft,
  hasDraftContent,
  mergeDraftIntoTranslations,
  readPublishDraft,
  writePublishDraft,
} from "../features/publish_course/publishDraftStorage";
import { checkMissingTranslations } from "./publishTranslationCheck";
import {
  TRANSLATION_FAILED_KEY,
  TRANSLATION_FAILURE_TOAST_MS,
} from "../helpers/translationFeedback";

export enum PublishStep {
  Initialize = 0,
  EnterCourseDetails = 1,
  Submitting = 2,
  Finished = 3,
  Failed = 4,
}

export const DEFAULT_ESTIMATED_TIME_IN_SECONDS = 120;

export const usePublishCourse = () => {
  const { t } = useTranslation();
  const orgId = useSelector(selectCurrentOrganization)?.id;
  const token = useSelector(selectToken);
  const trainingId = useSelector(selectCurrentTrainingId);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<PublishStep>(
    PublishStep.Initialize
  );
  const { saveTraining } = useSaveTraining();
  const localizedLanguages = useSelector(selectLocalizedLanguages);
  const refLang = useSelector(selectReferenceLanguage);
  const [translations, setTranslations] = useState<
    TranslationWithDescriptionDTO[]
  >([]);
  const [estimatedTimeInSecondsInput, setEstimatedTimeInSecondsInput] =
    useState<string>(DEFAULT_ESTIMATED_TIME_IN_SECONDS.toString());
  const [isInputInvalid, setIsInputInvalid] = useState<boolean>(true);
  const [publishFailure, setPublishFailure] = useState<PublishFailure | null>(
    null
  );
  const isPublishingRef = useRef<boolean>(false);
  const hasLoadedRef = useRef<boolean>(false);
  const loadedTrainingIdRef = useRef<string>("");
  const localizedEntries = useSelector(selectLocalizedEntries) ?? {};
  const propLocalizedEntries = useSelector(selectPropLocalizedEntries) ?? {};
  const takes = useSelector(selectTakes) ?? [];
  const takePositions = useSelector(selectEnvironmentInteractionPoints) ?? [];
  const propPresets = useSelector(selectAllPropPresets) ?? [];
  const usedProps = useSelector(selectUsedProps) ?? {};
  const startingViewError = takes.flatMap((take) =>
    getTakeStartingViewErrors(take as Take3D, takePositions, propPresets, usedProps)
      .map((message) => `${take.name}: ${message}`)
  )[0];

  const environmentId: string | undefined = useSelector(
    selectCurrentEnvironmentId
  );

  // Helper functions
  const createEmptyTranslation = (
    languageId: string
  ): TranslationWithDescriptionDTO => ({
    name: "",
    description: "",
    languageId,
  });

  const sortTranslationsByRefLang = (
    translations: TranslationWithDescriptionDTO[]
  ): TranslationWithDescriptionDTO[] => {
    return [...translations].sort((a, b) => {
      if (a.languageId === refLang) return -1;
      if (b.languageId === refLang) return 1;
      return 0;
    });
  };

  const getReferenceTexts = (): [string, string] => {
    const reference = translations.find((t) => t.languageId === refLang);
    if (!reference) {
      throw new Error("Reference language not found in translations");
    }
    return [reference.name, reference.description];
  };

  useEffect(() => {
    setIsInputInvalid(CheckIsInputInvalid());
  }, [translations, estimatedTimeInSecondsInput]);

  const onChangeDto = (dto: TranslationWithDescriptionDTO) => {
    setTranslations((prev) =>
      prev.map((t) => (t.languageId === dto.languageId ? { ...dto } : t))
    );
  };

  const onChangeEstimatedTimeInSeconds = (value: string) => {
    setEstimatedTimeInSecondsInput(value);
  };

  const getEstimatedTimeInSeconds = (): number | undefined => {
    const trimmedValue = estimatedTimeInSecondsInput.trim();

    if (!/^\d+$/.test(trimmedValue)) {
      return undefined;
    }

    const parsedValue = Number(trimmedValue);
    return parsedValue > 0 ? parsedValue : undefined;
  };

  const publishTraining = async () => {
    if (!orgId) return;
    if (isPublishingRef.current) return;
    if (startingViewError) {
      toast.error(startingViewError);
      return;
    }

    const estimatedTimeInSeconds = getEstimatedTimeInSeconds();
    if (estimatedTimeInSeconds === undefined) {
      setIsInputInvalid(true);
      return;
    }

    isPublishingRef.current = true;
    setPublishFailure(null);
    setCurrentStep(PublishStep.Submitting);

    try {
      // validate translations -> checks if all required translations are present (nl, fr, en)
      // if not present, autotranslated from ref lang and added
      //
      // A failure here used to be invisible: the service resolved with an empty
      // array and the course published with undefined names and descriptions.
      // Stop instead — the previously published version stays live.
      let validatedTranslations;
      try {
        validatedTranslations = await validateTranslations();
      } catch (error) {
        setPublishFailure({
          code: PUBLISH_TRANSLATION_FAILED_CODE,
          message: "",
          status: 0,
        });
        setCurrentStep(PublishStep.Failed);
        return;
      }

      // save training first
      await saveTraining();

      // publish training
      const result = await TrainingService.PublishCourse(
        orgId,
        token,
        trainingId,
        validatedTranslations,
        environmentId === "not-set" ? undefined : environmentId,
        estimatedTimeInSeconds
      );

      // 200/201 = success. Everything else carries an error code we can show,
      // including the 409s, which mean "assets are not ready yet".
      // Note: status 0 means there was no response, so it must not pass as success.
      if (result.status >= 200 && result.status <= 201) {
        // Published: the API is the source of truth from here on.
        clearPublishDraft(trainingId);
        setCurrentStep(PublishStep.Finished);
        return;
      }

      setPublishFailure(classifyPublishFailure(result));
      setCurrentStep(PublishStep.Failed);
    } finally {
      isPublishingRef.current = false;
    }
  };

  const CheckIsInputInvalid = () => {
    const hasInvalidTranslation = translations.some(
      (t) => !t.name || t.name.trim() === ""
    );

    return hasInvalidTranslation || getEstimatedTimeInSeconds() === undefined;
  };

  /** Display names of the languages this publish includes, reference first. */
  const publishedLanguages = useMemo(
    () =>
      localizedLanguages
        .filter((l) => l.includeInPublish ?? true)
        .sort((a, b) => {
          if (a.shortcode === refLang) return -1;
          if (b.shortcode === refLang) return 1;
          return 0;
        })
        .map((l) => l.full),
    [localizedLanguages, refLang]
  );

  // Check if all keys from reference language are filled in for all other languages
  const hasMissingTranslations = useMemo(
    () =>
      checkMissingTranslations(
        localizedEntries,
        propLocalizedEntries,
        localizedLanguages,
        refLang
      ),
    [localizedEntries, propLocalizedEntries, localizedLanguages, refLang]
  );

  // set up translations
  const getCourseDetails = async () => {
    if (!orgId) return;

    setIsLoading(true);
    try {
      const details = await TrainingService.GetCourseDetails(
        orgId,
        token,
        trainingId
      );

      // Filter to only include languages with includeInPublish=true
      const publishableLanguages = localizedLanguages.filter(
        (l) => l.includeInPublish ?? true
      );
      const publishableLanguageCodes = publishableLanguages.map((l) => l.shortcode);

      let resultTranslations: TranslationWithDescriptionDTO[];

      if (!details) {
        // Create empty translations for all publishable languages
        resultTranslations = publishableLanguages.map((l) =>
          createEmptyTranslation(l.shortcode)
        );
        setEstimatedTimeInSecondsInput(
          DEFAULT_ESTIMATED_TIME_IN_SECONDS.toString()
        );
      } else {
        // Filter translations to only include publishable languages
        const filteredTranslations = details.translations.filter((t) =>
          publishableLanguageCodes.includes(t.languageId)
        );

        // Find publishable languages that are not in details.translations
        const existingLanguageIds = new Set(
          filteredTranslations.map((t) => t.languageId)
        );
        const missingLanguages = publishableLanguages
          .filter((l) => !existingLanguageIds.has(l.shortcode))
          .map((l) => createEmptyTranslation(l.shortcode));

        // Combine filtered translations with missing languages
        resultTranslations = [...filteredTranslations, ...missingLanguages];
        setEstimatedTimeInSecondsInput(
          details.estimatedTimeInSeconds?.toString() ??
            DEFAULT_ESTIMATED_TIME_IN_SECONDS.toString()
        );
      }

      // Anything typed before a failed publish attempt is still on this
      // machine; fill the gaps the API left with it.
      const draft = readPublishDraft(trainingId);
      resultTranslations = mergeDraftIntoTranslations(resultTranslations, draft);
      if (!details && draft?.estimatedTimeInSecondsInput) {
        setEstimatedTimeInSecondsInput(draft.estimatedTimeInSecondsInput);
      }

      // Sort so refLang is first
      setTranslations(sortTranslationsByRefLang(resultTranslations));
      setCurrentStep(PublishStep.EnterCourseDetails);
      hasLoadedRef.current = true;
      loadedTrainingIdRef.current = trainingId;
    } finally {
      setIsLoading(false);
    }
  };

  // Keep the draft in step with what is on screen, but only once the initial
  // load is done - otherwise the empty starting state overwrites a real draft.
  //
  // The dialog stays mounted for the life of the editor, so trainingId can
  // change while `translations` still holds the previous training's text.
  // Writing then would file one training's draft under another's key, so the
  // draft is only touched while state and id still belong together.
  useEffect(() => {
    if (!hasLoadedRef.current) return;
    if (loadedTrainingIdRef.current !== trainingId) return;

    if (!hasDraftContent(translations)) {
      clearPublishDraft(trainingId);
      return;
    }

    writePublishDraft(trainingId, {
      translations,
      estimatedTimeInSecondsInput,
    });
  }, [translations, estimatedTimeInSecondsInput, trainingId]);

  const validateTranslations = async () => {
    const requiredLangs = ["nl", "en", "fr"];
    const existingLanguageIds = new Set(translations.map((t) => t.languageId));
    const missingLangs = requiredLangs.filter(
      (lang) => !existingLanguageIds.has(lang)
    );

    if (missingLangs.length === 0) {
      return translations;
    }

    const texts = getReferenceTexts();

    const translated = await Promise.all(
      missingLangs.map(async (lang) => {
        const translated = await TranslationService.TranslateTexts(
          token,
          texts,
          refLang,
          lang
        );

        return {
          name: translated[0],
          description: translated[1],
          languageId: lang,
        } as TranslationWithDescriptionDTO;
      })
    );

    return [...translations, ...translated];
  };

  /**
   * The dialog's translate buttons are fired without awaiting, so a rethrow
   * would only become an unhandled rejection.
   */
  const reportTranslationFailure = () =>
    toast.error(t(TRANSLATION_FAILED_KEY) as string, {
      position: "top-right",
      autoClose: TRANSLATION_FAILURE_TOAST_MS,
    });

  const translateAll = async () => {
    setIsLoading(true);
    try {
      const texts = getReferenceTexts();

      const translated = await Promise.all(
        translations.map(async (t) => {
          if (t.languageId === refLang) {
            return t;
          }

          const translated = await TranslationService.TranslateTexts(
            token,
            texts,
            refLang,
            t.languageId
          );

          return {
            name: translated[0],
            description: translated[1],
            languageId: t.languageId,
          };
        })
      );

      setTranslations(translated);
    } catch (error) {
      // Leaves the existing course name and description alone. Before the
      // service propagated failures this wrote `undefined` over both.
      reportTranslationFailure();
    } finally {
      setIsLoading(false);
    }
  };

  const translateMissing = async () => {
    setIsLoading(true);
    try {
      const texts = getReferenceTexts();

      const translated = await Promise.all(
        translations.map(async (t) => {
          if (t.languageId === refLang) {
            return t;
          }

          const translated = await TranslationService.TranslateTexts(
            token,
            texts,
            refLang,
            t.languageId
          );

          return {
            name: isNullOrWhitespace(t.name) ? translated[0] : t.name,
            description: isNullOrWhitespace(t.description)
              ? translated[1]
              : t.description,
            languageId: t.languageId,
          };
        })
      );

      setTranslations(translated);
    } catch (error) {
      reportTranslationFailure();
    } finally {
      setIsLoading(false);
    }
  };

  return {
    currentStep,
    getCourseDetails,
    translations,
    onChangeDto,
    estimatedTimeInSecondsInput,
    onChangeEstimatedTimeInSeconds,
    isEstimatedTimeInSecondsInvalid:
      getEstimatedTimeInSeconds() === undefined,
    publishTraining,
    isInputInvalid,
    startingViewError,
    translateAll,
    translateMissing,
    isLoading,
    hasMissingTranslations,
    publishFailure,
    retryPublish: publishTraining,
    publishedLanguages,
    estimatedTimeInSeconds: getEstimatedTimeInSeconds(),
  } as const;
};
